/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';
import { FaPen, FaInfoCircle } from 'react-icons/fa';

import { IconButton } from '../../../../../styles/ui/Button';
import {
   ONBOARDING_STATUS,
   ONBOARDING_STATUS_LABEL,
   ONBOARDING_PIPELINE,
   ADMISSION_DOCUMENT_STATUS,
   BACKGROUND_CHECK_STATUS_LABEL,
   BACKGROUND_CHECK_SUB_ANALYSES,
   ADMISSION_EXAM_STATUS_LABEL,
   PRE_CADASTRO_COUNTABLE_FIELDS,
   countPreCadastroFilled,
   isPreCadastroComplete,
} from '../../constants';
import { formatCpf, formatDateBr } from '../../utils/format';
import { hasManualFields } from '../manualSteps';
import { Card, CardHead, Rail, FactRow, ProgItem, InfoNote } from '../styles';

// ---------------------------------------------------------------------------
// Rail lateral da admissão (mock: Resumo + Progresso + nota). O progresso lê o
// estado real de cada etapa; etapas PASSADAS com campos manuais ganham o
// lápis de edição retroativa (abre o ManualStepModal — decisão 2026-07-14).
// ---------------------------------------------------------------------------

function progressStatusFor(step, { onboarding, documents }) {
   switch (step) {
      case ONBOARDING_STATUS.COLETA_DOCS: {
         const relevant = documents.filter(
            (doc) => doc.status !== ADMISSION_DOCUMENT_STATUS.NAO_APLICA,
         );
         const received = relevant.filter(
            (doc) =>
               doc.status === ADMISSION_DOCUMENT_STATUS.RECEBIDO ||
               doc.status === ADMISSION_DOCUMENT_STATUS.ENVIADO_CONTABILIDADE,
         );
         return `${received.length}/${relevant.length}`;
      }
      case ONBOARDING_STATUS.BACKGROUND_CHECK:
         // Agregado DERIVADO no backend (0067 §2.2): REPROVADO se alguma
         // sub-análise reprovou; APROVADO se ambas; senão PENDENTE.
         return (
            BACKGROUND_CHECK_STATUS_LABEL[onboarding?.background_check_status] ??
            'Pendente'
         );
      case ONBOARDING_STATUS.PRE_CADASTRO: {
         if (isPreCadastroComplete(onboarding)) return 'Completo';
         const filled = countPreCadastroFilled(onboarding);
         return `Rascunho · ${filled} de ${PRE_CADASTRO_COUNTABLE_FIELDS.length}`;
      }
      case ONBOARDING_STATUS.EXAME_ADMISSIONAL:
         return (
            ADMISSION_EXAM_STATUS_LABEL[onboarding?.admission_exam_status] ??
            'Pendente'
         );
      case ONBOARDING_STATUS.RASCUNHO_ONVIO:
         return onboarding?.onvio_draft_done ? 'Feito' : '—';
      case ONBOARDING_STATUS.ENVIO_CONTABILIDADE:
         return onboarding?.sent_to_accounting_at
            ? formatDateBr(onboarding.sent_to_accounting_at)
            : '—';
      case ONBOARDING_STATUS.AGUARDANDO_INICIO:
         return onboarding?.start_date
            ? formatDateBr(onboarding.start_date)
            : '—';
      default:
         return '—';
   }
}

function dotStateFor(step, status) {
   if (status === ONBOARDING_STATUS.ADMITIDO) return 'ok';
   const stepIndex = ONBOARDING_PIPELINE.indexOf(step);
   const statusIndex = ONBOARDING_PIPELINE.indexOf(status);
   if (statusIndex === -1) return 'wait';
   if (stepIndex < statusIndex) return 'ok';
   if (stepIndex === statusIndex) return 'now';
   return 'wait';
}

export default function AdmissionRail({
   onboarding,
   candidate,
   documents = [],
   responsibleName = null,
   canEditPastSteps = false,
   onEditStep,
}) {
   const status = onboarding?.status;

   return (
      <Rail>
         <Card>
            <CardHead>
               <h2>Resumo da admissão</h2>
            </CardHead>
            <FactRow>
               <span className="k">Empresa</span>
               <span className={`v${onboarding?.company_entity ? '' : ' empty'}`}>
                  {onboarding?.company_entity ?? 'A definir'}
               </span>
            </FactRow>
            <FactRow>
               <span className="k">Cargo</span>
               <span
                  className={`v${onboarding?.job_position ? '' : ' empty'}`}
               >
                  {onboarding?.job_position?.position_name ?? 'A definir'}
               </span>
            </FactRow>
            <FactRow>
               <span className="k">Data de início</span>
               <span className={`v${onboarding?.start_date ? '' : ' empty'}`}>
                  {onboarding?.start_date
                     ? formatDateBr(onboarding.start_date)
                     : '—'}
               </span>
            </FactRow>
            <FactRow>
               <span className="k">Responsável do RH</span>
               <span className={`v${responsibleName ? '' : ' empty'}`}>
                  {responsibleName ?? '—'}
               </span>
            </FactRow>
            <FactRow>
               <span className="k">CPF</span>
               <span className={`v${candidate?.cpf ? '' : ' empty'}`}>
                  {formatCpf(candidate?.cpf)}
               </span>
            </FactRow>
         </Card>

         <Card>
            <CardHead>
               <h2>Progresso da admissão</h2>
            </CardHead>
            {ONBOARDING_PIPELINE.map((step) => {
               const dotState = dotStateFor(step, status);
               const canEdit =
                  canEditPastSteps &&
                  dotState === 'ok' &&
                  hasManualFields(step);
               return (
                  <React.Fragment key={step}>
                     <ProgItem $state={dotState}>
                        <span className="dot2" />
                        <span className="lbl">
                           {ONBOARDING_STATUS_LABEL[step]}
                        </span>
                        <span className="st">
                           {progressStatusFor(step, { onboarding, documents })}
                           {canEdit && (
                              <IconButton
                                 type="button"
                                 style={{ width: 26, height: 26 }}
                                 title={`Editar · ${ONBOARDING_STATUS_LABEL[step]}`}
                                 aria-label={`Editar ${ONBOARDING_STATUS_LABEL[step]}`}
                                 onClick={() => onEditStep(step)}
                              >
                                 <FaPen size={11} />
                              </IconButton>
                           )}
                        </span>
                     </ProgItem>

                     {/* Sub-itens: as 2 análises do Background Check (0067) */}
                     {step === ONBOARDING_STATUS.BACKGROUND_CHECK &&
                        BACKGROUND_CHECK_SUB_ANALYSES.map((analysis) => {
                           const result = onboarding?.[analysis.statusKey];
                           let subState = 'wait';
                           if (result === 'APROVADO') subState = 'ok';
                           else if (result === 'REPROVADO') subState = 'bad';
                           return (
                              <ProgItem
                                 key={analysis.statusKey}
                                 $state={subState}
                                 $sub
                              >
                                 <span className="dot2" />
                                 <span className="lbl">
                                    {analysis.shortLabel}
                                 </span>
                                 <span className="st">
                                    {BACKGROUND_CHECK_STATUS_LABEL[result] ??
                                       'Pendente'}
                                 </span>
                              </ProgItem>
                           );
                        })}
                  </React.Fragment>
               );
            })}
         </Card>

         <InfoNote>
            <FaInfoCircle size={18} />
            <p>
               Os passos manuais (antecedentes, exame admissional, envio à
               contabilidade) aparecem na própria etapa, quando ela fica ativa —
               não antes.
            </p>
         </InfoNote>
      </Rail>
   );
}

AdmissionRail.propTypes = {
   onboarding: PropTypes.shape({
      status: PropTypes.string,
      company_entity: PropTypes.string,
      job_position: PropTypes.shape({ position_name: PropTypes.string }),
      start_date: PropTypes.string,
   }),
   candidate: PropTypes.shape({ cpf: PropTypes.string }),
   documents: PropTypes.arrayOf(PropTypes.shape({})),
   responsibleName: PropTypes.string,
   canEditPastSteps: PropTypes.bool,
   onEditStep: PropTypes.func,
};
